/**
 * Which publisher a subscriber follows when a stage has more than one (`R-OBS-6`).
 *
 * > *Viewers and the panel monitor follow exactly one publisher: the most recent to publish. Two
 * > publishers on a stage never produce mixed picture or sound.*
 *
 * A stage has two publishers during a **hand-over** — the host's browser to OBS, or one laptop to
 * another — and before this the player merged every publisher's tracks into one `MediaStream`:
 * two pictures competing for one element and both voices at once. The rule is the simplest one
 * that makes a hand-over work without anybody telling the room it is happening: whoever started
 * publishing last is who the host meant to be the camera, and when they stop, whoever is still
 * publishing is better than nothing.
 *
 * **A copy of `apps/customer/src/room/publishers.ts`**, for the reason `useMonitor.ts` is a copy of
 * the room's `useViewer.ts`. The two must state the same rule; a monitor that followed a different
 * publisher from the room would show the host a picture their audience is not seeing.
 *
 * Pure, like `playback.ts` beside it, so the rule is tested without a stage
 * (`apps/host/test/publishers.test.ts`). `useMonitor` holds the tracks and asks this which
 * participant's to attach.
 *
 * A switch needs nothing from `playback.ts`: the newcomer's frame counter starts again from zero,
 * and `readPlayback` takes a lower reading as the new baseline and the next frame as progress, so
 * a hand-over costs one sample rather than reading as an interruption. The test pins that.
 */

export interface Publishing {
  /**
   * When this participant began publishing, as an order rather than a time: a counter the hook
   * advances each time a participant it was not holding streams for adds one. Higher is newer.
   */
  since: number;
}

/**
 * The participant to follow: the newest publisher still holding streams, or `undefined` when
 * there is none.
 */
export function followed(
  publishers: ReadonlyMap<string, Publishing>,
): string | undefined {
  let newest: string | undefined;
  let since = -Infinity;
  for (const [id, one] of publishers) {
    if (one.since > since) {
      newest = id;
      since = one.since;
    }
  }
  return newest;
}
